/**
 * 统一组件解析器
 * 将 TemplateNodeRenderer 中的 import.meta.glob 逻辑提取为模块级单例，
 * 避免重复 glob 扫描。
 */

import type { Component } from 'vue'

const toKebabCase = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase()

const toLooseCase = (value: string) =>
  toKebabCase(value).replace(/-/g, '')

const modules = import.meta.glob(
  [
    '~/components/**/*.vue',
    '!~/components/TemplateNodeRenderer.vue',
    '!~/components/TemplateRenderer.vue',
    '!~/components/renderer/**',
  ],
  { eager: true },
)

const componentMap = new Map<string, Component>()
const componentNameSet = new Set<string>()

Object.entries(modules).forEach(([path, mod]) => {
  const fileName = path.split('/').pop() || ''
  const baseName = fileName.replace(/\.vue$/i, '')
  if (!baseName) return

  let component: Component | undefined
  try {
    component = (mod as { default?: Component }).default || (mod as Component)
  } catch (e) {
    console.warn(`[componentResolver] 跳过循环依赖组件: ${path}`, e)
    return
  }
  if (!component) return

  componentMap.set(baseName, component)
  componentMap.set(toKebabCase(baseName), component)
  componentMap.set(toLooseCase(baseName), component)
  componentNameSet.add(baseName)
})

/**
 * 解析本地 Vue 组件。
 * 同时支持 PascalCase 和 kebab-case。
 */
export function resolveLocalComponent(name: string): Component | undefined {
  return componentMap.get(name) || componentMap.get(toKebabCase(name)) || componentMap.get(toLooseCase(name))
}

/** 获取所有本地组件名集合（PascalCase） */
export function getLocalComponentNames(): ReadonlySet<string> {
  return componentNameSet
}

/** 获取完整的组件 Map */
export function getComponentMap(): ReadonlyMap<string, Component> {
  return componentMap
}

export { toKebabCase }
